// Generate a small JPEG thumbnail using canvas. Target ~100KB, 800px longest side.

export interface ThumbResult {
  blob: Blob;
  width: number;
  height: number;
}

// iOS Safari's createImageBitmap throws on some HEIC and edge-case JPEGs. Fall
// back to <img>+canvas which is slower but far more tolerant.
async function decodeAny(source: Blob): Promise<{ w: number; h: number; draw: (ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, dw: number, dh: number) => void; close: () => void }> {
  try {
    const bmp = await createImageBitmap(source);
    return {
      w: bmp.width,
      h: bmp.height,
      draw: (ctx, dw, dh) => ctx.drawImage(bmp, 0, 0, dw, dh),
      close: () => bmp.close?.(),
    };
  } catch {
    const url = URL.createObjectURL(source);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('image decode failed'));
        i.decoding = 'async';
        i.src = url;
      });
      return {
        w: img.naturalWidth,
        h: img.naturalHeight,
        draw: (ctx, dw, dh) => ctx.drawImage(img, 0, 0, dw, dh),
        close: () => URL.revokeObjectURL(url),
      };
    } catch (e) {
      URL.revokeObjectURL(url);
      throw e;
    }
  }
}

export async function makeThumbnail(file: File, maxDim = 800, quality = 0.82): Promise<ThumbResult> {
  try {
    const src = await decodeAny(file);
    const scale = Math.min(1, maxDim / Math.max(src.w, src.h));
    const w = Math.round(src.w * scale);
    const h = Math.round(src.h * scale);
    const blob = await drawToBlob(src, w, h, quality);
    src.close();
    return { blob, width: w, height: h };
  } catch {
    // Decode failed — likely iOS Safari canvas memory limit or an odd HEIC.
    // Fall back to a small placeholder so the tile still renders. The full
    // file bytes upload to Drive untouched.
    return { blob: await placeholderThumb(), width: 24, height: 24 };
  }
}

// Client-side compression before queueing. Keeps memory in check on old iPhones.
// Targets ~2MB. If the file is already small enough or we can't decode it,
// returns the original blob unchanged — better to upload a big photo than to
// drop it entirely.
export async function compressForUpload(file: File, targetMaxBytes = 2 * 1024 * 1024): Promise<Blob> {
  if (file.size <= targetMaxBytes) return file;
  try {
    const src = await decodeAny(file);
    const scale = Math.min(1, Math.sqrt(targetMaxBytes / file.size)) * 0.95;
    const w = Math.round(src.w * scale);
    const h = Math.round(src.h * scale);
    const blob = await drawToBlob(src, w, h, 0.85);
    src.close();
    return blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

export async function placeholderThumb(): Promise<Blob> {
  const size = 32;
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(size, size);
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#c58a5a';
      ctx.fillRect(0, 0, size, size);
      return canvas.convertToBlob({ type: 'image/jpeg', quality: 0.7 });
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new Blob([new Uint8Array()], { type: 'image/jpeg' });
  ctx.fillStyle = '#c58a5a';
  ctx.fillRect(0, 0, size, size);
  return new Promise<Blob>((resolve) =>
    canvas.toBlob(
      (b) => resolve(b ?? new Blob([new Uint8Array()], { type: 'image/jpeg' })),
      'image/jpeg',
      0.7
    )
  );
}

async function drawToBlob(
  src: { draw: (ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, dw: number, dh: number) => void },
  w: number,
  h: number,
  quality: number
): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    src.draw(ctx, w, h);
    return canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  src.draw(ctx, w, h);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob null'))),
      'image/jpeg',
      quality
    )
  );
}
